/*
 * quality-tier.js — export-quality ceilings per plan, as a Premium funnel.
 *
 * THE MODEL (Farid 2026-07-19): the Microsoft/Adobe pattern — do NOT spoil the
 * render, CLOSE the option. Clipchamp and Adobe Express refuse to export when a
 * premium asset is used rather than stamping a watermark on the result; that is
 * the most user-respectful shape found across the whole competitor sweep, and it
 * is what this module implements.
 *
 * ⚠️ THE RULE THIS MODULE EXISTS TO OBEY (feedback_no_silent_quality_downgrade,
 * Farid 2026-07-07): «Нельзя пользователю без его ведома искажать качество на
 * ниже… Это обман.» So this module NEVER silently clamps a chosen value. It
 * DISABLES the out-of-reach options up front, so the ceiling is visible BEFORE
 * the user commits. In the one case where a value can arrive already-too-high —
 * a setting persisted by __toolSettings from before the ceiling existed, or from
 * an expired Premium — it moves the selection down AND renders a persistent
 * visible line saying so. Never a toast: a toast can be missed, and a missed
 * downgrade is exactly the deception the rule forbids.
 *
 * HONEST SCOPE: tier separation, NOT DRM — same as tool-usage-limit.js and the
 * TTS HD-permit. Every capped tool encodes in the user's own browser, so a
 * JS-savvy user can re-enable an option from devtools. Fine. The job is a clear
 * tier boundary and an upgrade path, not enforcement.
 *
 * CEILING SOURCE: window.__tierLimits.quality — server-resolved per tier
 * (config/tiers.php 'quality' → User::tierLimits → session-bootstrap; guests via
 * the generated __anonTierLimits() fallback in layouts/app.blade.php). Ceilings
 * apply only where Premium is SOLD; elsewhere the layout emits null and this
 * module FAILS OPEN (uncapped) — no paywall friction where there is no upsell.
 *
 * ⚠️ FAIL-OPEN IS DELIBERATE. A missing/!broken ceiling must never brick a free
 * tool. Same invariant as tool-usage-limit.js cap(). It is also why the server
 * fallback in User::tierLimits() derives from $isPaid instead of [].
 */
(function () {
    'use strict';

    /* ⛔ NO ENCODER-QUALITY KIND. A rank→CRF map lived here from 2026-07-19 and
     * never had a caller; on 2026-07-29 it was removed rather than wired. Giving
     * the free tier a worse CRF at the SAME resolution spoils the render instead
     * of closing an option — the exact opposite of this module's model — it is
     * unsellable ("you get CRF 18"), and it produces no impression/bump event,
     * so it could never be measured. Rationale in full: config/tiers.php, the
     * quality block comment. */

    /* Ceiling key per gate kind — all compared as the raw numeric value of the
     * <option>. */
    var KIND_KEY = {
        kbps:   'audio_max_kbps',
        bits:   'audio_max_bits',
        hz:     'audio_max_hz',
        height: 'video_max_height',
        side:   'image_max_side',
        // GIF export axes (video-to-gif pilot). Both are compared as the raw
        // numeric value of the <option> (fps / colour count) — no rank/source
        // wording, every option carries a plain number.
        fps:    'gif_max_fps',
        colors: 'gif_max_colors',
        // Screen-recorder capture ceilings (2026-08-20). Separate kinds — the
        // recorder must NOT ride 'height' (global video_max_height=720, the
        // approved recorder deal is 1080p) nor 'fps' (gif_max_fps=15).
        // 'rec-quality' ranks the word-valued quality <select> via
        // data-quality-value attrs (lossless 4 · high 3 · medium 2 · compact 1);
        // its plan line is suppressed by design — Премиум carries no
        // record_max_qrank, so paidCap()=Infinity and the line never renders
        // («up to rank 4» is not a sentence). The lock badge on the option is
        // the disclosure, plus the tool's own free-limits line.
        'rec-fps':     'record_max_fps',
        'rec-quality': 'record_max_qrank',
        // Live-capture resolution (webcam-recorder, 2026-09-04). Same approved
        // deal as the screen recorder — 1080p free — so it rides
        // record_max_height, not the global 720p export axis: every one of the
        // 1552 sessions a month saw a padlock on 1080p that /screen-recorder
        // never showed.
        'rec-height':  'record_max_height'
    };

    function tier() {
        return (window.__tierLimits && typeof window.__tierLimits === 'object') ? window.__tierLimits : null;
    }

    /* Premium status must come from the RUNTIME probe, never a Blade-inlined
     * constant: tool pages are cached by Cloudflare with no Vary:Cookie, so a
     * paying user is routinely served anonymous HTML. __isProNow() is the
     * canonical resolver (layouts/app.blade.php) and every functional premium
     * gate is required to consult it — enforced by
     * tests/Feature/PremiumRuntimeGateInvariantTest. */
    function isPro() {
        try {
            if (window.__isProNow) return !!window.__isProNow(false);
            var t = tier();
            return !!(t && t.is_pro);
        } catch (_) { return false; }
    }

    /* ⚠️⚠️ «Нет данных» — третье состояние, а не «без ограничений».
     *
     * Снимок тарифа приезжает АСИНХРОННО (/api/session-bootstrap) — и у гостя,
     * и у вошедшего: до ответа `window.__tierLimits` не существует вовсе. Первый
     * проход гейта (DOMContentLoaded, раньше любого ответа) видел cap() = Infinity
     * и уходил в ветку «Премиум»: открывал опции и ВОЗВРАЩАЛ запомненный выбор с
     * тостом «Вернули 1080p — до вашего тарифа это было ограничено». Через долю
     * секунды снимок приезжал, гейт снова запирал 1080p и снова запоминал его —
     * так что на КАЖДОМ визите не-Премиум читал ложь, а выставить обещанное не мог.
     * Обращение #1658 (13.09.2026, /audio-to-video): «пишет что вернули формат для
     * моего тарифа 1080..по факту выставить его не даёт». Замер 31.08–15.09:
     * 3 801 такой тост у гостей/verified/registered на RU и 273 на intl, в 30+
     * тулах (vocal-remover 1 384, extract-audio 887, join 337) — против 38
     * настоящих возвратов после покупки.
     *
     * Поэтому гейт различает ТРИ случая: снимка ещё нет → не трогать ничего и
     * ждать (tier-bootstrap:ready, поздний проход и аварийный гостевой снимок
     * позовут снова); Премиум → открыть и вернуть запомненное; потолок здесь не
     * продаётся (снимок есть, quality = null) → открыть молча, ничего не
     * «возвращая». Fail-open для экспорта (clamp) не меняется: без данных
     * значение не зажимается. */
    function hasSnapshot() { return !!tier(); }

    /* The ceiling for a kind, or Infinity when uncapped/unknown (fail-open). */
    function cap(kind) {
        /* A tool that trades the ceiling for a daily cap of one file
           (config/tiers.php `quality_full_tools`, layout → __QT_FULL): every axis
           of THIS page is open to everyone — options unlocked silently (the gate's
           «not sold here» branch: no «restored» toast, no plan line), «Original»
           not cut by ResolutionModule, clamp() a no-op. Фарид 08.10.2026. */
        if (window.__QT_FULL === true) return Infinity;
        var t = tier();
        var q = t && t.quality;
        if (!q || typeof q !== 'object') return Infinity;   // no data ⇒ uncapped
        var v = q[KIND_KEY[kind]];
        if (typeof v !== 'number' || !(v > 0)) return Infinity;
        return v;
    }

    /*
     * Потолок С УЧЁТОМ ИСХОДНОГО МАТЕРИАЛА — `data-qt-source="<число>"`.
     *
     * Потолок продаёт КАЧЕСТВО ЭКСПОРТА. Значение не выше того, что у человека
     * уже есть в файле, качества не добавляет: это конверсия его собственного
     * материала, зачастую понижающая. Закрывать такую опцию — не воронка, а
     * поломка, и до 2026-08-13 она была видна на /convert невооружённым глазом:
     *
     *   источник FLAC 192 кГц, тир free (audio_max_hz = 48000)
     *     «Авто» (= сохранить 192 кГц) — открыто и БЕСПЛАТНО отдаёт 192000 Гц
     *                                     (замерено: файл на выходе 192 кГц)
     *     «96k»                        — ЗАКРЫТО бейджем Премиума
     *
     * То есть тир продавал ровно тот вариант, который ХУЖЕ уже доступного
     * даром. Причина — та же, что и у трёх дефектов в convert-tool.js: гейт
     * сравнивал абсолютное число с константой, потому что про источник не знал
     * ничего. Атрибут — единственный способ рассказать ему про файл, не таща
     * файловую логику в общий модуль.
     *
     * ⚠️ Осознанно НЕ применяется там, где атрибута нет. У kbps/bits «оставить
     * как в источнике» — платная ось по прямому решению Фарида (2026-07-19,
     * «cap source too»), и делать там поблажку по источнику значило бы отдать
     * платную ось даром. Атрибут ставит только тот тул, который знает, что его
     * ось — понижающая; сейчас это частота на /convert.
     */
    function effectiveCap(sel, kind) {
        var ceiling = cap(kind);
        if (!sel || ceiling === Infinity) return ceiling;
        var raw = sel.getAttribute && sel.getAttribute('data-qt-source');
        if (!raw) return ceiling;
        var srcVal = parseFloat(raw);
        if (!isFinite(srcVal) || !(srcVal > 0)) return ceiling;
        return Math.max(ceiling, srcVal);
    }

    /* Numeric value carried by an <option>. Prefers an explicit
     * data-quality-value (for labels like "high" or "1080p HD"), else parses
     * leading digits out of the value attribute. Returns null when the option
     * carries no comparable number (e.g. "source", "original", "auto") — those
     * are NEVER gated: they mean "don't re-encode", which cannot exceed the
     * user's own material. */
    function optionValue(opt) {
        var raw = opt.getAttribute('data-quality-value');
        if (raw === null) raw = opt.value;
        if (raw === null || raw === undefined) return null;
        var m = String(raw).match(/-?\d+(?:\.\d+)?/);
        return m ? parseFloat(m[0]) : null;
    }

    /* ── funnel telemetry ──────────────────────────────────────────────────────
     * The gate CLOSES options rather than showing a banner, so a free user who
     * wants premium quality leaves no click to count — the disabled option is
     * un-clickable by design. Without a signal the whole quality-tier program is
     * unmeasurable (the gap flagged in project_gif_quality_tier_pilot). So the
     * gate itself reports, from ONE place that covers every quality-gated tool:
     *   quality_gate_impression — a free user was SHOWN >=1 locked option (reach:
     *     how many sessions even encounter the premium ceiling on this tool/kind)
     *   quality_gate_bump — the user DELIBERATELY moved the control to the top
     *     free option while premium ones sit locked above (intent: they maxed the
     *     free tier and are blocked from more — the strongest demand proxy)
     * Both are deduped once per (tool|kind) per page so a re-gate (bootstrap/
     * repopulate/settings-open) can't inflate the count. Cross-reference with
     * pro_checkout_view by session_id for a rough conversion read. */
    var _impressed = Object.create(null);
    var _bumped = Object.create(null);
    var _srcClamped = Object.create(null);
    var _ceilNoted = Object.create(null);
    /* Kinds whose SPECIFIC sentence ("your file is 193 kbps, this copy is 128")
       is on screen — in the shared block, or at the tool's own spot when the
       tool asked sourceClamped(…, {render:false}). One fact, one sentence: while
       it stands, the block carries neither the generic plan line nor the
       «quality lowered to X» note for that kind (Фарид 08.10.2026, /add-music:
       the field said «ваш звук 320 → в видео 128», and the block under the
       button repeated «качество снижено до 128» in general words). */
    var _srcSaid = Object.create(null);
    /* The «lowered» note's text per kind, kept while the specific sentence
       withholds it — so it can return when that sentence goes away. */
    var _loweredText = Object.create(null);
    function curSlug() { return (window.__TOOL_SLUG || 'unknown'); }
    function emitImpression(kind, locked, ceiling) {
        var key = curSlug() + '|' + kind;
        if (_impressed[key]) return;
        _impressed[key] = 1;
        try { window.__ae && window.__ae('feature_use', { slug: curSlug(), f: 'quality_gate_impression', kind: kind, locked: locked, top: ceiling }); } catch (_) {}
    }
    function bestAllowedValue(sel, ceiling) {
        var best = null;
        for (var i = 0; i < sel.options.length; i++) {
            var v = optionValue(sel.options[i]);
            if (v !== null && v <= ceiling && (best === null || v > best)) best = v;
        }
        return best;
    }
    // One change-listener per gated select: fires ONCE when the user lands on the
    // top free option while locked options exist above it. Attached only on the
    // free path (a Pro user has nothing locked). NOT `disabled=` — see the note in
    // tool-usage-limit.js; here the select is fully interactive, we only observe.
    function wireBump(sel, kind) {
        if (sel.__qtBumpWired) return;
        sel.__qtBumpWired = true;
        sel.addEventListener('change', function () {
            try {
                // The gate's own move-down dispatches a synthetic change — skip it.
                if (sel.__qtSuppressBump) { sel.__qtSuppressBump = 0; return; }
                if (isPro()) return;
                var ceiling = effectiveCap(sel, kind);
                if (ceiling === Infinity) return;
                var hasLockedAbove = false;
                for (var i = 0; i < sel.options.length; i++) {
                    var v = optionValue(sel.options[i]);
                    if (v !== null && v > ceiling) { hasLockedAbove = true; break; }
                }
                if (!hasLockedAbove) return;
                var selOpt = sel.options[sel.selectedIndex];
                var selV = selOpt ? optionValue(selOpt) : null;
                var best = bestAllowedValue(sel, ceiling);
                if (selV === null || best === null || selV !== best) return;
                var key = curSlug() + '|' + kind;
                if (_bumped[key]) return;
                _bumped[key] = 1;
                window.__ae && window.__ae('feature_use', { slug: curSlug(), f: 'quality_gate_bump', kind: kind, top: ceiling });
            } catch (_) {}
        });
    }

    /* Localized inline, same pattern as QualityModule/ChannelModule — the module
     * must work on every page with zero per-tool translation wiring. The plan is
     * «Премиум» in Russian, never «Pro» (feedback_plan_naming_premium_not_pro). */
    var LABELS = {
        badge: {
            en: 'Premium', ru: 'Премиум', ko: '프리미엄', ar: 'بريميوم', pt: 'Premium',
            id: 'Premium', de: 'Premium', es: 'Premium', fr: 'Premium', uk: 'Преміум', tr: 'Premium'
        },
        /* :v is the label of the option we actually landed on. Naming the new
         * value is the point — "quality was reduced" without saying to WHAT is
         * still the vague half of the deception the rule forbids. */
        lowered: {
            en: 'Quality lowered to :v. Higher is available on Premium.',
            ru: 'Качество снижено до :v. Выше — на Премиуме.',
            ko: '품질이 :v(으)로 낮아졌습니다. 더 높은 품질은 프리미엄에서 가능합니다.',
            ar: 'تم خفض الجودة إلى :v. الجودة الأعلى متاحة في بريميوم.',
            pt: 'Qualidade reduzida para :v. Superior disponível no Premium.',
            id: 'Kualitas diturunkan ke :v. Lebih tinggi tersedia di Premium.',
            de: 'Qualität auf :v reduziert. Höher ist mit Premium verfügbar.',
            es: 'Calidad reducida a :v. Superior disponible en Premium.',
            fr: 'Qualité réduite à :v. Supérieure disponible avec Premium.',
            uk: 'Якість знижено до :v. Вища — у Преміумі.',
            tr: 'Kalite :v olarak düşürüldü. Daha yükseği Premium’da mevcut.'
        },
        /* ⭐ Возврат зажатого выбора после апгрейда (2026-08-31).
         *
         * До этого зажим работал только вниз. Живой случай: человек
         * зарегистрировался, через две минуты купил Премиум, а через два часа
         * написал в поддержку «почему такое низкое качество видео
         * скачивается». Проект был начат ДО оплаты, гейт опустил разрешение до
         * потолка бесплатного тира и записал 720p в черновик; после оплаты
         * восстановление черновика вернуло это же 720p, и поднять его обратно
         * было некому. Поддержка ответила «вы сами выбрали 720» — то есть
         * молчаливое понижение успело превратиться в чужую вину.
         *
         * Замер, на котором это видно и без обращения: за 30 дней на
         * /slideshow-maker 12 107 показов гейта — ВСЕ с потолком 720 — и 66%
         * всех выдач ушли в 720p при значении по умолчанию 1080p. */
        restored: {
            en: 'Restored :v — this was capped before your plan.',
            ru: 'Вернули :v — до вашего тарифа это было ограничено.',
            ko: ':v(으)로 되돌렸습니다 — 요금제 이전에는 제한되어 있었습니다.',
            ar: 'تمت استعادة :v — كان مقيّدًا قبل خطتك.',
            pt: ':v restaurado — estava limitado antes do seu plano.',
            id: ':v dipulihkan — sebelumnya dibatasi.',
            de: ':v wiederhergestellt — vorher war das begrenzt.',
            es: ':v restaurado: antes estaba limitado.',
            fr: ':v rétabli — c’était limité auparavant.',
            uk: 'Повернули :v — до вашого тарифу це було обмежено.',
            tr: ':v geri yüklendi — planınızdan önce bu sınırlıydı.'
        },
        cta: {
            en: 'Learn more', ru: 'Подробнее', ko: '자세히', ar: 'المزيد', pt: 'Saiba mais',
            id: 'Selengkapnya', de: 'Mehr erfahren', es: 'Más información', fr: 'En savoir plus', uk: 'Докладніше', tr: 'Daha fazla bilgi'
        },
        /* Said when the value we lowered was the user's OWN material: they chose
         * "source", the source is above the ceiling, and the copy they are
         * holding is smaller than what they brought. The ceiling line above
         * ("your plan exports X") is generic and sits before the run; this one
         * carries their own two numbers and is only true afterwards. Farid
         * 2026-09-18: a non-Premium user must SEE the lower quality, and that
         * is where the Premium case is made. */
        src_clamped: {
            en: 'Your file is :src — on your plan this copy is :out. :plan keeps the source quality, up to :top.',
            ru: 'Ваш файл — :src, а эта копия :out: столько даёт ваш тариф. Исходное качество сохраняет :plan — до :top.',
            ko: '원본은 :src인데 이 사본은 :out입니다 — 현재 요금제의 상한입니다. :plan에서는 원본 품질 그대로, 최대 :top까지 유지됩니다.',
            ar: 'ملفك :src، وهذه النسخة :out — هذا سقف خطتك. مع :plan تبقى جودة المصدر كما هي، حتى :top.',
            id: 'File Anda :src, salinan ini :out — itu batas paket Anda. Di :plan kualitas sumber dipertahankan, hingga :top.',
            pt: 'Seu arquivo é :src e esta cópia é :out — é o teto do seu plano. No :plan a qualidade original é mantida, até :top.',
            de: 'Deine Datei hat :src, diese Kopie :out — das ist die Grenze deines Tarifs. Mit :plan bleibt die Quellqualität erhalten, bis :top.',
            es: 'Tu archivo es :src y esta copia es :out: es el tope de tu plan. Con :plan se conserva la calidad original, hasta :top.',
            fr: 'Votre fichier est à :src, cette copie à :out — c’est le plafond de votre offre. Avec :plan la qualité source est conservée, jusqu’à :top.',
            uk: 'Ваш файл — :src, а ця копія :out: стільки дає ваш тариф. Вихідну якість зберігає :plan — до :top.',
            tr: 'Dosyanız :src, bu kopya :out — planınızın tavanı bu. :plan ile kaynak kalite korunur, :top değerine kadar.'
        },
        /* Suffix appended to a "match input"/"source" option so its ceiling is
         * disclosed at the point of choice — a free user picking "Source" must
         * SEE that the encode is capped, otherwise capping it (Farid 2026-07-19:
         * cap source too) would be the silent downgrade the rule forbids. */
        cap_kbps:   { en: 'up to :n kbps', ru: 'до :n kbps', ko: '최대 :n kbps', ar: 'حتى :n kbps', pt: 'até :n kbps', id: 'hingga :n kbps', de: 'bis :n kbps', es: 'hasta :n kbps', fr: 'jusqu’à :n kbps', uk: 'до :n kbps', tr: 'en fazla :n kbps' },
        cap_bits:   { en: 'up to :n-bit', ru: 'до :n-бит', ko: '최대 :n비트', ar: 'حتى :n بت', pt: 'até :n bits', id: 'hingga :n bit', de: 'bis :n Bit', es: 'hasta :n bits', fr: 'jusqu’à :n bits', uk: 'до :n-біт', tr: 'en fazla :n bit' },
        cap_height: { en: 'up to :np', ru: 'до :np', ko: '최대 :np', ar: 'حتى :np', pt: 'até :np', id: 'hingga :np', de: 'bis :np', es: 'hasta :np', fr: 'jusqu’à :np', uk: 'до :np', tr: 'en fazla :np' },
        cap_side:   { en: 'up to :n px', ru: 'до :n px', ko: '최대 :n px', ar: 'حتى :n بكسل', pt: 'até :n px', id: 'hingga :n px', de: 'bis :n px', es: 'hasta :n px', fr: 'jusqu’à :n px', uk: 'до :n px', tr: 'en fazla :n px' },
        /* ⭐ The ADVANCE disclosure (Farid 2026-07-29): «пользователи должны
         * заранее знать, что у них качество понижено будет, а у премиум аккаунта
         * качество на выбор и высокое». Locking the out-of-reach options only
         * speaks to someone who opens the dropdown; most people never do and
         * would meet the ceiling for the first time in the exported file. This
         * line sits under the control from the first paint. :own = this plan's
         * ceiling, :max = what the paid plan gives — BOTH resolved from the tier
         * snapshot, never written into a string. */
        plan_line: {
            en: 'Your plan exports :own. :plan: your choice, up to :max.',
            ru: 'Ваш тариф — экспорт :own. В :plan качество на выбор, до :max.',
            ko: '현재 요금제는 :own(으)로 내보냅니다. :plan에서는 :max까지 선택할 수 있습니다.',
            ar: 'خطتك تصدّر :own. مع :plan الجودة باختيارك حتى :max.',
            id: 'Paket Anda mengekspor :own. Di :plan kualitas bisa dipilih, hingga :max.',
            pt: 'Seu plano exporta :own. No :plan a qualidade é sua escolha, até :max.',
            de: 'Dein Tarif exportiert :own. Mit :plan wählst du frei, bis :max.',
            es: 'Tu plan exporta :own. En :plan la calidad es a tu elección, hasta :max.',
            fr: 'Votre formule exporte :own. Avec :plan, la qualité est au choix, jusqu’à :max.',
            uk: 'Ваш тариф — експорт :own. У :plan якість на вибір, до :max.',
            tr: 'Planınızın dışa aktarım sınırı: :own. :plan: kalite seçimi sizde, :max sınırına kadar.'
        }
    };

    function locale() {
        var l = (window.__LOCALE || document.documentElement.lang || 'en').toString();
        return l.substring(0, 2).toLowerCase();
    }

    function tr(key, repl) {
        var e = LABELS[key];
        if (!e) return key;
        var s = e[locale()] || e.en || key;
        if (repl) { Object.keys(repl).forEach(function (k) { s = s.replace(':' + k, repl[k]); }); }
        return s;
    }

    function strings() {
        return { badge: tr('badge'), cta: tr('cta') };
    }

    /* The paid plan's ceiling for a kind — the "up to :max" half of the advance
     * disclosure. Server-resolved as the MAX across every tier
     * (User::qualityCeiling → tierLimits.quality_max), so this file never knows
     * a number. Infinity when unknown ⇒ the line is not rendered at all: a
     * half-filled promise is worse than none. */
    function paidCap(kind) {
        var t = tier();
        var q = t && t.quality_max;
        if (!q || typeof q !== 'object') return Infinity;
        var v = q[KIND_KEY[kind]];
        if (typeof v !== 'number' || !(v > 0)) return Infinity;
        return v;
    }

    /* How a ceiling READS to a user. 'rank' is deliberately absent: an encoder
     * quality rank is an internal number and would say nothing — those controls
     * disclose through their locked options alone. */
    var UNIT = {
        kbps:   { en: ':n kbps', ru: ':n кбит/с', ko: ':n kbps', ar: ':n كbps', pt: ':n kbps', id: ':n kbps', de: ':n kbps', es: ':n kbps', fr: ':n kbps', uk: ':n кбіт/с', tr: ':n kbps' },
        bits:   { en: ':n-bit',  ru: ':n бит',    ko: ':n비트',   ar: ':n بت',    pt: ':n bits', id: ':n bit',  de: ':n Bit',  es: ':n bits', fr: ':n bits', uk: ':n біт', tr: ':n bit' },
        /* 'hz' is deliberately ABSENT — see capLabel(): the plan line would
         * claim a sample-rate ceiling that the "auto / keep source" path does
         * not enforce. The locked 96 000 option speaks for itself. */
        height: { en: ':np',     ru: ':np',       ko: ':np',     ar: ':np',      pt: ':np',     id: ':np',     de: ':np',     es: ':np',     fr: ':np',     uk: ':np', tr: ':np' },
        side:   { en: ':n px',   ru: ':n px',     ko: ':n px',   ar: ':n بكسل',  pt: ':n px',   id: ':n px',   de: ':n px',   es: ':n px',   fr: ':n px',   uk: ':n px', tr: ':n px' },
        fps:    { en: ':n fps',  ru: ':n к/с',    ko: ':n fps',  ar: ':n إطار/ث', pt: ':n fps',  id: ':n fps',  de: ':n fps',  es: ':n fps',  fr: ':n ips',  uk: ':n к/с', tr: ':n fps' },
        colors: { en: ':n colors', ru: ':n цветов', ko: ':n색', ar: ':n لون', pt: ':n cores', id: ':n warna', de: ':n Farben', es: ':n colores', fr: ':n couleurs', uk: ':n кольорів', tr: ':n renk' },
        'rec-fps': { en: ':n fps',  ru: ':n к/с',    ko: ':n fps',  ar: ':n إطار/ث', pt: ':n fps',  id: ':n fps',  de: ':n fps',  es: ':n fps',  fr: ':n ips',  uk: ':n к/с', tr: ':n fps' },
        /* 'rec-quality' deliberately ABSENT — see KIND_KEY comment. */
        'rec-height': { en: ':np',     ru: ':np',       ko: ':np',     ar: ':np',      pt: ':np',     id: ':np',     de: ':np',     es: ':np',     fr: ':np', uk: ':np', tr: ':np' }
    };
    function unitLabel(kind, n) {
        var e = UNIT[kind];
        if (!e) return null;
        return (e[locale()] || e.en).replace(':n', n);
    }

    /* The always-visible «your plan exports X · Premium: up to Y» line.
     *
     * Rendered ONCE per gated control, right under it, for FREE users on a plan
     * that actually sells an upgrade. Removed again the moment the tier snapshot
     * says the user is paid (they are not being sold anything) or the ceiling is
     * unknown (fail-open — never invent a restriction).
     *
     * Deliberately NOT a toast and NOT inside the dropdown: it has to be readable
     * without touching the control, because the whole point is that most users
     * never open it. */
    /* Where to hang the disclosure so it is actually READ.
     *
     * Straight after the control is right for a plain form row — including one
     * inside a panel that only appears after a file is picked (the line appears
     * together with the controls it describes, which is exactly the moment).
     *
     * It is WRONG when the control lives inside a pop-up: /denoise keeps its
     * bitrate list in an absolutely-positioned dropdown menu, so a line placed
     * there is visible only while the menu is open — i.e. to the very users who
     * already opened it and can see the locked options anyway. Climb out of any
     * absolutely/fixed-positioned wrapper first (bounded — never walk the page).
     */
    function planAnchor(el) {
        var node = el, cs, ps;
        for (var i = 0; i < 4 && node.parentNode && node.parentNode.nodeType === 1; i++) {
            // Never out of a wide-screen settings column (WideWorkspace): its work
            // area is a GRID while wide, and climbing out of it put the line under
            // the whole workspace, far from the select (/ringtone, 09.10.2026).
            if (node.parentNode.hasAttribute && node.parentNode.hasAttribute('data-wide-workspace')) break;
            try { cs = window.getComputedStyle(node); ps = window.getComputedStyle(node.parentNode); } catch (_) { break; }
            // Out of a pop-up (see above).
            if (cs.position === 'absolute' || cs.position === 'fixed') { node = node.parentNode; continue; }
            // Out of a single-line settings ROW. /convert lays bitrate, sample
            // rate and channels out in one non-wrapping flex row; a sibling <div>
            // dropped in there becomes a flex item and the sentence is squeezed
            // into a ~150px column between two dropdowns. Insert after the whole
            // row instead, where it reads as one line under the controls.
            if ((ps.display === 'flex' || ps.display === 'inline-flex') && ps.flexWrap === 'nowrap') { node = node.parentNode; continue; }
            if (ps.display === 'grid' || ps.display === 'inline-grid') { node = node.parentNode; continue; }
            break;
        }
        return node;
    }
    function planLineFor(sel) {
        var id = (sel.id || 'qt') + '__qtPlan';
        var el = document.getElementById(id);
        if (el) return el;
        el = document.createElement('div');
        el.id = id;
        el.className = 'qt-plan';
        el.setAttribute('data-qt-plan', '1');
        el.style.cssText = 'margin-top:6px;font-size:12px;line-height:1.45;color:var(--text-muted,#8b95a5)';
        var anchor = planAnchor(sel);
        if (anchor.parentNode) anchor.parentNode.insertBefore(el, anchor.nextSibling);
        return el;
    }
    /* Is there a shared plan/limit block on this page to publish into?
     * When there is, the ceiling belongs THERE — one box that states the plan,
     * the daily counter and every reduced parameter, with one CTA underneath.
     * Drawing a second line under each gated control (what this file used to do
     * unconditionally) meant a tool with both a cap and a quality ceiling said
     * "your plan is smaller" twice, in two places — 32 tools did exactly that
     * until 2026-07-31. */
    /* Что человек выбирал ДО зажима.
     *
     * Живёт в браузере, а не в проекте: проект после зажима уже содержит
     * опущенное значение — именно поэтому восстановление черновика и приносило
     * 720p обратно. Ключ — (инструмент, вид ограничения), чтобы «высота» на
     * одном туле не поднимала «битность» на другом.
     *
     * Не сохраняется, если хранилища нет: возврат — приятная поправка, а не
     * обязательство, и падать из-за приватного режима она не должна. */
    function wantedKey(kind) { return 'qt_wanted_' + (kind || 'x'); }
    function toolSlug() {
        var m = document.querySelector('meta[name="tool-slug"]');
        return (m && m.getAttribute('content')) || sharedBlock() || 'tool';
    }
    function rememberWanted(kind, value) {
        try {
            if (window.__toolSettings) window.__toolSettings.save(toolSlug(), wantedKey(kind), String(value));
        } catch (_) {}
    }
    function recallWanted(kind) {
        try {
            if (window.__toolSettings) return window.__toolSettings.load(toolSlug(), wantedKey(kind), null);
        } catch (_) {}
        return null;
    }
    function forgetWanted(kind) {
        try {
            if (window.__toolSettings) window.__toolSettings.save(toolSlug(), wantedKey(kind), null);
        } catch (_) {}
    }

    function sharedBlock() {
        if (!window.__toolLimit || typeof window.__toolLimit.setDetail !== 'function') return null;
        /* The mount that HAS the extras box, not the first mount in the DOM: a
           tool with the `variant="hint"` mirror above its full block (the rail
           of /remove-object-from-video, /video-crop) put the mirror first, the
           mirror carries no extras, and this answered null — the plan line then
           fell back to an inline sentence under the <select>, inside a folded
           <details> nobody opens. setDetail() itself already writes to every
           root that has the box. */
        var box = document.querySelector('[data-tool-limit] [data-tl-extras]');
        var root = box ? box.closest('[data-tool-limit]') : null;
        if (!root) return null;
        return root.getAttribute('data-tool-limit') || null;
    }

    function renderPlanLine(sel, kind, ceiling, opts) {
        var el = document.getElementById((sel.id || 'qt') + '__qtPlan');
        var top = paidCap(kind);
        var slug = sharedBlock();
        /* inline: render the sentence AT THE GIVEN ANCHOR even when a shared
           limit block exists. The default routing (below) publishes the line
           into that block — which is the right single home for the deep read,
           but the block is measurably unseen on tall tools (99.2% of its
           exposures report seen=0 on /slideshow-maker, 60 d), so a mirror at
           the decision point must be able to opt out of the routing. */
        var inline = !!(opts && opts.inline);

        /* Обещать можно только то, что ЭТОТ контрол способен выдать.
         *
         * paidCap('bits') = 32 (у Премиума 32-битный float для WAV). Но список
         * глубин у FLAC — [16, 24]: кодек v1.4 выше 24 не умеет, и quality-module
         * это знает. Строка же бралась из тира напрямую и у цели FLAC читалась
         * «Ваш тариф — экспорт 16 бит. В Премиум качество на выбор, до 32 бит» —
         * то есть воронка обещала глубину, которой в выпадающем списке нет и не
         * появится. Потолок продажи — максимум РЕАЛЬНЫХ опций контрола. */
        /* A bespoke anchor (planLine() into a div — /record's segmented picker,
           photo-filters, the slideshow export row) has no options; the clamp
           simply does not apply there. Reading .options unguarded threw on
           every such call, and both existing callers swallowed the throw in a
           try/catch — the advertised bespoke-anchor mode never worked at all. */
        var optList = sel.options || [];
        var best = null;
        for (var oi = 0; oi < optList.length; oi++) {
            var ov = optionValue(optList[oi]);
            if (ov !== null && (best === null || ov > best)) best = ov;
        }
        if (best !== null && best < top) top = best;
        /* …and only what THIS SOURCE can reach. A tool that never upscales
         * (video-compressor and the other nine ResolutionModule consumers)
         * offers 2160p in its list, so the clamp above leaves «до 2160p» on
         * a 1080p clip — a number no plan can deliver for that file. The
         * caller knows the source; opts.max is its shorter side (#1574,
         * 2026-09-04: «купил тариф, не могу улучшить до 2к и 4к»). */
        if (opts && typeof opts.max === 'number' && opts.max > 0 && opts.max < top) top = opts.max;

        /* Контрол мог СМЕНИТЬ ось — отзываем то, что публиковали под прежней.
         *
         * Строки в общий блок кладутся по ключу `quality:<kind>` (одна ось —
         * одна фраза, даже если её гейтят два контрола). Обратный случай —
         * ОДИН контрол, у которого ось меняется, — не отзывался никем.
         * На /convert это видно глазами: селектор «БИТРЕЙТ» у lossy-формата
         * гейтится как kbps, а стоит выбрать FLAC, тот же элемент становится
         * выбором РАЗРЯДНОСТИ и гейтится как bits. Строка про kbps оставалась
         * висеть, и на экране одновременно стояло «Ваш тариф — экспорт
         * 128 кбит/с» и «…16 бит» — причём первая про килобиты, которых у
         * FLAC не бывает вовсе. Замерено на проде 2026-08-13 с файлом 24/192.
         *
         * Ось «своя» у каждого элемента, поэтому помним её на нём же. */
        if (sel.__qtPlanKind && sel.__qtPlanKind !== kind) {
            if (slug) {
                window.__toolLimit.setDetail(slug, 'quality:' + sel.__qtPlanKind, '');
                window.__toolLimit.setDetail(slug, 'quality-lowered:' + sel.__qtPlanKind, '');
            }
            delete _lowered[sel.__qtPlanKind];
            delete _loweredText[sel.__qtPlanKind];
        }
        sel.__qtPlanKind = kind;

        // Nothing to disclose: unlimited here, unknown paid ceiling, the paid
        // plan is no better, or this kind has no user-readable unit (rank).
        if (ceiling === Infinity || top === Infinity || top <= ceiling || !UNIT[kind]) {
            if (el) el.style.display = 'none';
            if (slug) window.__toolLimit.setDetail(slug, 'quality:' + kind, '');
            return;
        }

        // Preferred path: hand the sentence to the shared block. Keyed by KIND,
        // so two gated controls of the same kind on one page (a format list and
        // a bitrate list both capping kbps) produce ONE line, not two.
        if (slug && !inline) {
            if (el) el.style.display = 'none';       // retract any legacy inline line
            // The lowered-note for this kind already states the ceiling and the
            // plan — a second sentence would just repeat it. So does the
            // specific «your file is X» sentence, wherever it stands.
            if (_lowered[kind] || _srcSaid[kind]) {
                window.__toolLimit.setDetail(slug, 'quality:' + kind, '');
                return;
            }
            window.__toolLimit.setDetail(slug, 'quality:' + kind, tr('plan_line', {
                own: unitLabel(kind, ceiling),
                max: unitLabel(kind, top),
                plan: tr('badge'),
            }));
            return;
        }

        // Fallback: tools that gate quality but have no daily cap (and therefore
        // no shared block) keep the inline line — it is their only disclosure.
        el = el || planLineFor(sel);
        // A bespoke anchor owns its line: the sentence goes INSIDE the given
        // element, not after it — the caller placed that element exactly where
        // the disclosure belongs (planAnchor()'s wrapper-walking is for real
        // <select>s living inside label rows).
        if (inline && !sel.options && el.parentNode !== sel) sel.appendChild(el);
        el.textContent = tr('plan_line', {
            own: unitLabel(kind, ceiling),
            max: unitLabel(kind, top),
            plan: tr('badge')
        }) + ' ';
        if (window.__PRO_HERE) {
            var a = document.createElement('a');
            a.href = proHref();
            a.textContent = tr('cta');
            a.style.cssText = 'color:var(--accent,#4da3ff);text-decoration:underline';
            wireCta(a, kind, 'ceiling', top);
            el.appendChild(a);
        }
        el.style.display = '';
    }
    /* Retract the disclosure from BOTH possible homes. Called when a user turns
       out to be paid (nothing left to disclose); missing the shared block here
       would leave a stale "your plan exports 720p" line sitting under the plan
       name of someone who just bought 4K. */
    function hidePlanLine(sel, kind) {
        var el = document.getElementById((sel.id || 'qt') + '__qtPlan');
        if (el) el.style.display = 'none';
        var slug = sharedBlock();
        if (slug && kind) window.__toolLimit.setDetail(slug, 'quality:' + kind, '');
    }

    /* The disclosure suffix for a "source" option, or '' when the kind has no
     * meaningful source-cap wording (hz/rank — those tools cap via an explicit
     * option, not a match-input choice). */
    function capLabel(kind, ceiling) {
        if (kind === 'kbps')   return tr('cap_kbps',   { n: ceiling });
        if (kind === 'bits')   return tr('cap_bits',   { n: ceiling });
        if (kind === 'height') return tr('cap_height', { n: ceiling });
        if (kind === 'side')   return tr('cap_side',   { n: ceiling });
        /* NOT 'hz': a sample-rate list's "auto" means "keep the source rate",
         * and nothing downsamples a 96 kHz master on that path. Annotating it
         * «up to 48000 Hz» would promise a cap we do not enforce — worse than
         * saying nothing. The explicit 96 000 option is closed with the plan
         * badge, which is the honest half of the disclosure. */
        return '';
    }

    /* Restore a "source" option's label if we had annotated it with a cap. */
    function unmarkSource(opt) {
        if (opt.getAttribute('data-qt-srccap') === '1') {
            if (opt.dataset.qtSrcbase !== undefined) { opt.textContent = opt.dataset.qtSrcbase; delete opt.dataset.qtSrcbase; }
            opt.removeAttribute('data-qt-srccap');
        }
    }

    /* Единственный обработчик клика по ссылке апселла — оба места зовут его.
     *
     * ⚠️ ЗАЧЕМ. Воронка потолка качества была ИЗМЕРИМА только на входе:
     * `quality_gate_impression` (311 тыс/мес) и `quality_gate_bump` (4 тыс/мес)
     * есть, а КЛИКА нет ни одного — он писался только в localStorage-атрибуцию
     * (`__proSrc`), и то лишь у ОДНОЙ из двух ссылок: у второй, которая
     * появляется сразу после «мы понизили вам настройку» (то есть у самой
     * намеренной), не было обработчика вовсе.
     *
     * Из-за этого запрос «сколько кликов по потолку» честно возвращал ноль —
     * не потому что не жали, а потому что поле не пишется. Замер 2026-08-20:
     * из точных меток на кассе Премиума суточный лимит даёт 758 сессий/мес,
     * потолок качества — 11. Понять, слабый это рычаг или просто слепой,
     * без этого события нельзя.
     *
     * `where` различает две ссылки: 'ceiling' — строка «ваш тариф X, в Премиум
     * до Y»; 'lowered' — «мы опустили вашу настройку до X». Ожидания у них
     * разные, и складывать их в одно число значит снова ничего не увидеть. */
    function wireCta(a, kind, where, ceiling) {
        a.addEventListener('click', function () {
            try { window.__proSrc && window.__proSrc.set(curSlug() + '-quality'); } catch (_) {}
            try {
                window.__ae && window.__ae('feature_use', {
                    slug: curSlug(), f: 'quality_gate_cta',
                    kind: kind || null, where: where, top: (ceiling === undefined ? null : ceiling)
                });
            } catch (_) {}
        });
    }

    function proHref() {
        // __PRO_URL = layout-echoed pro_checkout_url() (locale-scoped). The old
        // hardcoded '/'+loc+'/pro' does NOT exist on RU (funnel = /ru/pro/checkout;
        // /{locale}/pro is intl-Polar only) — every quality-wall CTA 404'd there.
        var loc = window.__LOCALE || 'en';
        return window.__PRO_URL || ('/' + loc + '/pricing');
    }

    /* The persistent visible line rendered under a select whose selection had to
     * be moved down. Deliberately a DOM node, not __toast. */
    function noteFor(sel) {
        var id = (sel.id || 'qt') + '__qtNote';
        var el = document.getElementById(id);
        if (el) return el;
        el = document.createElement('div');
        el.id = id;
        el.className = 'qt-note';
        el.setAttribute('role', 'status');
        el.style.cssText = 'margin-top:6px;font-size:12px;line-height:1.4;color:var(--text-secondary,#9aa)';
        // Тот же якорь, что у plan-строки (planLineFor): выйти из однострочного
        // flex-ряда или ГРИДА и встать ПОД ним, а не отдельной колонкой сбоку.
        // Раньше нота вставлялась прямо после select и в сетке (новый блок
        // экспорта слайдшоу, /convert) висела узким столбцом (Фарид 2026-09-01).
        var anchor = planAnchor(sel);
        if (anchor.parentNode) anchor.parentNode.insertBefore(el, anchor.nextSibling);
        return el;
    }

    /* Kinds whose selection was moved down. The "we lowered it to X, higher is on
       Премиум" sentence already contains the ceiling AND the plan, so printing the
       plain "your plan exports X · Премиум up to Y" line next to it says the same
       thing twice — which is the duplication this whole change removes. */
    var _lowered = Object.create(null);

    function showLoweredNote(sel, landedLabel, kind) {
        if (kind) _lowered[kind] = 1;
        // Same rule as the ceiling line: when the shared plan block exists, this
        // belongs THERE, so a tool never carries two "your plan is smaller"
        // messages in two places. It stays a SEPARATE keyed row rather than being
        // merged into the ceiling line — "we moved your setting down" is a
        // different fact from "your ceiling is X", and collapsing them would hide
        // that something was changed (feedback_no_silent_quality_downgrade).
        var slug = sharedBlock();
        if (slug) {
            var text = tr('lowered', { v: landedLabel });
            if (kind) _loweredText[kind] = text;
            // The specific sentence about the person's own file already says
            // it; the note comes back if that sentence goes (sourceClamped → null).
            if (kind && _srcSaid[kind]) return;
            window.__toolLimit.setDetail(slug, 'quality-lowered:' + (kind || 'x'), text);
            return;
        }
        var el = noteFor(sel);
        el.textContent = tr('lowered', { v: landedLabel }) + ' ';
        // Only link to the plan where it is actually sold; elsewhere the note
        // still explains the change but offers no dead-end upsell.
        if (window.__PRO_HERE) {
            var a = document.createElement('a');
            a.href = proHref();
            a.textContent = tr('cta');
            a.style.cssText = 'color:var(--accent,#4da3ff);text-decoration:underline';
            wireCta(a, kind, 'lowered');
            el.appendChild(a);
        }
        el.style.display = '';
    }

    /* The plan line as a shared-block ROW, with no <select> to hang it under —
       for tools whose quality is a tool default rather than a control. Silent
       when a stronger sentence is already up, or when this kind is not sold. */
    function publishPlanRow(kind) {
        var slug = sharedBlock();
        if (!slug) return false;
        if (_lowered[kind] || _srcSaid[kind]) return false;
        var ceiling = cap(kind), top = paidCap(kind);
        if (ceiling === Infinity || top === Infinity || top <= ceiling || !UNIT[kind]) return false;
        try {
            window.__toolLimit.setDetail(slug, 'quality:' + kind, tr('plan_line', {
                own: unitLabel(kind, ceiling), max: unitLabel(kind, top), plan: tr('badge')
            }));
        } catch (_) { return false; }
        return true;
    }

    /* Does this page offer a LOSSY audio output at all? The ceiling only bites
       there, and the answer has to come from the page itself — the alternative
       is a per-tool list, which is exactly the kind of list that goes stale the
       day someone adds a tool. */
    var LOSSY_OUT = /^(mp3|aac|m4a|ogg|oga|opus|wma|mp2|m4r|ac3|eac3|amr)$/i;
    function pageOffersLossyAudio() {
        try {
            var nodes = document.querySelectorAll('option[value], [data-fmt], [data-format]');
            for (var i = 0; i < nodes.length; i++) {
                var v = nodes[i].getAttribute('data-fmt') || nodes[i].getAttribute('data-format') || nodes[i].value;
                if (v && LOSSY_OUT.test(String(v).trim())) return true;
            }
        } catch (_) {}
        return false;
    }

    function clearNote(sel, kind) {
        if (kind) { delete _lowered[kind]; delete _loweredText[kind]; }
        var el = document.getElementById((sel.id || 'qt') + '__qtNote');
        if (el) el.style.display = 'none';
        // Retract the shared row too, or a "we lowered your setting" line would
        // outlive the setting it referred to.
        var slug = sharedBlock();
        if (slug && kind) window.__toolLimit.setDetail(slug, 'quality-lowered:' + kind, '');
    }

    /*
     * gate(select, kind) — close every option above this tier's ceiling.
     *
     *   kind: 'kbps' | 'bits' | 'hz' | 'height' | 'rank'
     *
     * Idempotent: safe to call again when the tier snapshot lands, when the tool
     * repopulates the <select>, or on every open of a settings panel.
     */
    /* Выбор ЧЕЛОВЕКА (не наш возврат и не синхронизация тула — у тех change
     * синтетический) при Премиуме — единственное, что делает память о
     * «хотел» ненужной: дальше его значение и есть желаемое. У бесплатного
     * выбор под потолком ничего не говорит о том, чего он хотел НАД ним. */
    function wireWantForget(sel, kind) {
        if (sel.__qtWantWired) return;
        sel.__qtWantWired = true;
        sel.addEventListener('change', function (e) {
            try {
                if (!e || !e.isTrusted) return;
                if (!isPro()) return;
                forgetWanted(sel.getAttribute('data-quality-gate') || kind);
            } catch (_) {}
        });
    }

    function gate(sel, kind) {
        if (!sel || !sel.options || !KIND_KEY[kind]) return;
        wireWantForget(sel, kind);
        var pro = isPro();
        // No tier snapshot yet and nothing says «paid»: we do not know the
        // ceiling, so neither lock, nor unlock, nor restore — see hasSnapshot().
        if (!pro && !hasSnapshot()) return;
        var ceiling = effectiveCap(sel, kind);

        // Pro/unlimited: actively UNDO any previous gating. This branch matters —
        // the tier snapshot arrives asynchronously, so a Premium user can be
        // gated by the guest fallback for a few hundred ms before their real
        // ceiling lands. Without this, they'd stay locked out of what they paid for.
        if (pro || ceiling === Infinity) {
            for (var i = 0; i < sel.options.length; i++) {
                var o = sel.options[i];
                if (o.getAttribute('data-qt-locked') === '1') {
                    o.disabled = false;
                    o.removeAttribute('data-qt-locked');
                    if (o.dataset.qtLabel) { o.textContent = o.dataset.qtLabel; delete o.dataset.qtLabel; }
                }
                unmarkSource(o);
            }
            clearNote(sel, kind);
            hidePlanLine(sel, kind);   // a paying user is not being sold anything
            // Потолок вырос — вернуть то, что зажимали. Условия намеренно
            // узкие: поднимаем ТОЛЬКО если текущее значение и есть то, до
            // которого мы опускали, иначе человек мог выбрать 720 сам и мы бы
            // переписали его осознанный выбор.
            // ⚠️ И только когда тариф ДЕЙСТВИТЕЛЬНО платный: «потолок здесь не
            // продаётся» — не апгрейд, и фраза «до вашего тарифа это было
            // ограничено» там неправда (см. hasSnapshot()).
            /* ⚠️ Память о «хотел» живёт, пока человек САМ не выберет значение.
             *
             * Обращение #8048 (26.09.2026): Премиум куплен через апселл
             * разрешения, пять экспортов после оплаты — все 720p. Черновик,
             * сохранённый под бесплатным потолком, «Восстановить» ставил
             * ПРОГРАММНО (value без события), и гейт этого не видел вовсе — ни
             * один его проход после восстановления не случался; а если возврат
             * успевал сработать раньше (у неё в 11:56:56), память стиралась
             * тут же, и позднее восстановление опускать было уже нечем
             * поднимать. RU за 90 дней: у купивших через слайдшоу 31
             * восстановление черновика в 13 сессиях и всего 2 возврата
             * разрешения. Теперь память стирает только выбор человека
             * (настоящий change, см. wireWantForget) или явный выбор в диалоге
             * тула (__qualityTier.chosen), а тул, программно поставивший
             * значение (восстановление проекта, отмена), зовёт recheck().
             * Сторож — tests/playwright/slideshow-upgrade-keeps-the-wanted-resolution.mjs. */
            var wanted = pro ? recallWanted(kind) : null;
            // Сами по себе проходы гейта поднимают значение ОДИН раз за просмотр
            // страницы: раньше память после этого стиралась, и тул, который
            // потом опускал значение сам (смена формата пересобирает список),
            // не получал повторных «Вернули…». Повторно — только когда тул
            // попросил сам (recheck после восстановления черновика).
            var mayRestore = !sel.__qtRestored || sel.__qtRecheck;
            if (wanted && mayRestore && String(sel.value) !== String(wanted)) {
                var target = null;
                for (var q = 0; q < sel.options.length; q++) {
                    if (String(sel.options[q].value) === String(wanted)) { target = sel.options[q]; break; }
                }
                var wv = target ? optionValue(target) : null;
                var cv = optionValue(sel.options[sel.selectedIndex]);
                if (target && !target.disabled && wv !== null && cv !== null && wv > cv) {
                    sel.value = target.value;
                    sel.__qtRestored = true;
                    try {
                        sel.__qtSuppressBump = 1;
                        sel.dispatchEvent(new Event('change', { bubbles: true }));
                    } catch (_) {}
                    // Сказать вслух: изменение настройки без уведомления —
                    // то же самое молчание, что и при понижении, только в
                    // другую сторону.
                    var lbl = (target.dataset.qtLabel || target.textContent || '').trim();
                    if (window.__toast) window.__toast(tr('restored', { v: lbl }), 'success', 7000);
                    if (window.__ae) window.__ae('feature_use', { f: 'quality_restored_after_upgrade', kind: kind, to: String(target.value) });
                }
            }
            return;
        }

        var badge = strings().badge;
        /* Подпись «Исходное · до N» — это РАСКРЫТИЕ ограничения, а значит она
         * уместна только там, где ограничение есть. Если платный план не выше
         * нашего (ось перестали продавать) или его потолок неизвестен, продавать
         * нечего, и суффикс превращается в шум, притворяющийся честностью.
         * Ровно эта проверка уже стоит у plan-строки (renderPlanLine:
         * `top <= ceiling` → не показывать) — здесь её не было, и снятие
         * потолка оставило бы на экране «Исходное · до 32-бит» при списке,
         * где 32 бита и так открыты. */
        var paidTop = paidCap(kind);
        var srcSuffix = (paidTop === Infinity || paidTop <= ceiling) ? '' : capLabel(kind, ceiling);
        var bestAllowed = null;
        var selectedWasLocked = false;
        var lockedCount = 0;

        for (var j = 0; j < sel.options.length; j++) {
            var opt = sel.options[j];
            var val = optionValue(opt);

            // "source"/"original"/"auto" — a match-input choice. It stays
            // SELECTABLE (it never exceeds the user's own material), but the
            // encode is capped to the ceiling (see quality-module getArgs), so
            // its label must disclose that cap. Farid 2026-07-19: cap source too.
            // `data-qt-copy="1"` — a choice that gives the same on every plan:
            // the stream copied as it is, or a fixed number every plan has
            // (/convert «Авто»). «up to N» there would promise a cap that does
            // not happen.
            if (val === null) {
                if (srcSuffix && opt.getAttribute('data-qt-copy') !== '1') {
                    if (opt.getAttribute('data-qt-srccap') !== '1') {
                        opt.dataset.qtSrcbase = opt.textContent;
                        opt.textContent = opt.textContent + ' · ' + srcSuffix;
                        opt.setAttribute('data-qt-srccap', '1');
                    }
                } else {
                    // Потолок исчез или вырос — снять прежнюю подпись. Без этой
                    // ветки «до 16-бит» пережила бы и снятие потолка, и
                    // повышение: суффикс ставился один раз и не отзывался
                    // никогда (отзыв был только на Премиум-ветке выше).
                    unmarkSource(opt);
                }
                continue;
            }

            if (val > ceiling) {
                if (opt.getAttribute('data-qt-locked') !== '1') {
                    opt.dataset.qtLabel = opt.textContent;
                    opt.textContent = opt.textContent + ' · ' + badge;
                    opt.setAttribute('data-qt-locked', '1');
                }
                opt.disabled = true;
                lockedCount++;
                if (opt.selected) selectedWasLocked = true;
            } else {
                if (opt.getAttribute('data-qt-locked') === '1') {
                    opt.disabled = false;
                    opt.removeAttribute('data-qt-locked');
                    if (opt.dataset.qtLabel) { opt.textContent = opt.dataset.qtLabel; delete opt.dataset.qtLabel; }
                }
                if (bestAllowed === null || val > optionValue(bestAllowed)) bestAllowed = opt;
            }
        }

        // A persisted setting (or an expired Premium) can leave the selection on
        // a now-locked option. Move it down to the best allowed AND say so
        // visibly — this is the ONLY path that changes a user's chosen value,
        // and it must never be silent.
        if (selectedWasLocked && bestAllowed) {
            // Запомнить ЧЕГО человек хотел — до того, как значение будет
            // затёрто. Без этой строки апгрейд не может ничего вернуть:
            // прежний выбор к тому моменту не существует нигде.
            rememberWanted(kind, sel.value);
            sel.value = bestAllowed.value;
            // Use the option's own visible label ("128 kbps" / "16-bit"), not the
            // raw value — the note must read the way the control reads.
            showLoweredNote(sel, (bestAllowed.dataset.qtLabel || bestAllowed.textContent || '').trim(), kind);
            // Remember WHAT we lowered them to. gate() runs several times per
            // pageview (bootstrap, observer, the 1.8 s late pass), and on every
            // run after the first the selection is already allowed — so the
            // `else` below used to erase the note ~1.8 s after it appeared.
            // MEASURED 2026-07-31: a user with a saved 320 kbps got 128 with the
            // explanation visible for under two seconds, i.e. the module written
            // to forbid silent downgrades was performing one, on every gated
            // tool. The note may only go when the user has moved on themselves.
            sel.__qtLoweredTo = String(bestAllowed.value);
            try {
                // This move-down is OURS, not the user's — mark it so wireBump's
                // change-listener doesn't miscount the synthetic event as a
                // deliberate "maxed the free tier" bump.
                sel.__qtSuppressBump = 1;
                sel.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (_) {}
        } else if (!selectedWasLocked) {
            if (sel.__qtLoweredTo === undefined || String(sel.value) !== sel.__qtLoweredTo) {
                sel.__qtLoweredTo = undefined;
                clearNote(sel, kind);
            }
        }

        // The ADVANCE disclosure — before the export, without opening the
        // dropdown. Rendered whenever this tier is actually capped, even if no
        // option happens to sit above the ceiling on this particular control:
        // the user still deserves to know the export is limited.
        renderPlanLine(sel, kind, ceiling);

        // Funnel reach + intent (free user only reaches here; the Pro/unlimited
        // path returned above). Report ONCE per (tool|kind) that a premium ceiling
        // was shown, and start watching for a deliberate bump to the top free step.
        if (lockedCount > 0) {
            emitImpression(kind, lockedCount, ceiling);
            wireBump(sel, kind);
        }

        // A tool that repopulates its <select> after we gated it (format switch,
        // device enumeration, async catalogue) would hand the user ungated
        // options. Re-gate on any option change — one observer per control,
        // guarded against our own writes.
        if (!sel.__qtObserved && typeof MutationObserver === 'function') {
            sel.__qtObserved = true;
            var mo = new MutationObserver(function () {
                if (sel.__qtGating) return;
                sel.__qtGating = true;
                // Read the kind back off the element: a tool that repopulates
                // may have switched it (QualityModule stamps 'bits' for WAV/FLAC
                // and 'kbps' for lossy). Re-gating a fresh kbps list with the
                // stale 'bits' ceiling would lock every option.
                try { gate(sel, sel.getAttribute('data-quality-gate') || kind); } finally { sel.__qtGating = false; }
            });
            try { mo.observe(sel, { childList: true, subtree: true }); } catch (_) {}
        }
    }

    /*
     * gateGroup(container, kind, attr) — the same gate for a BUTTON/CHIP picker.
     *
     * Half the tools pick quality with a row of chips, not a <select>
     * (mp3/aac/m4a/ogg/audio-compressor, denoise, trim, volume, silence-remover,
     * add-silence, video-trim, /record's segmented tiers). Writing the gate
     * twelve times is how the "shared primitive" rots; declaring it is one
     * attribute:
     *
     *     <div data-quality-gate-group="kbps" data-qt-attr="data-bitrate"> … </div>
     *
     * Each child carrying that attribute with a numeric value above the ceiling
     * becomes unclickable + badged. If the ACTIVE chip was one of them, the best
     * allowed chip is CLICKED (the tool's own handler applies it — the module
     * must not know how a given tool stores its choice) and the move is disclosed
     * by the same persistent line the selects get. Non-numeric values ("source",
     * "auto") are never locked — they mean "don't re-encode".
     */
    function gateGroup(box, kind, attr) {
        if (!box || !KIND_KEY[kind]) return;
        var pro = isPro();
        if (!pro && !hasSnapshot()) return;   // ceiling unknown yet — see hasSnapshot()
        attr = attr || box.getAttribute('data-qt-attr') || 'data-bitrate';
        var ceiling = cap(kind);
        var chips = box.querySelectorAll('[' + attr + ']');
        var i, chip, raw, val;

        if (pro || ceiling === Infinity) {
            for (i = 0; i < chips.length; i++) {
                chip = chips[i];
                if (chip.getAttribute('data-qt-locked') !== '1') continue;
                chip.disabled = false;
                chip.removeAttribute('aria-disabled');
                chip.removeAttribute('data-qt-locked');
                chip.style.removeProperty('opacity');
                chip.style.removeProperty('cursor');
                chip.style.removeProperty('pointer-events');
                var b = chip.querySelector('.qt-lock');
                if (b) b.remove();
            }
            hidePlanLine(box, kind);
            return;
        }

        var badge = strings().badge;
        var best = null, bestVal = -1, activeLocked = null, locked = 0;
        for (i = 0; i < chips.length; i++) {
            chip = chips[i];
            raw = chip.getAttribute(attr);
            val = /^-?\d+(\.\d+)?$/.test(String(raw).trim()) ? parseFloat(raw) : null;
            if (val === null) continue;                       // "source" / "auto"
            if (val > ceiling) {
                if (chip.getAttribute('data-qt-locked') !== '1') {
                    chip.setAttribute('data-qt-locked', '1');
                    var s = document.createElement('span');
                    s.className = 'qt-lock';
                    s.textContent = ' · ' + badge;
                    s.style.cssText = 'font-size:12px;font-weight:700;color:var(--accent,#4da3ff)';
                    chip.appendChild(s);
                }
                chip.disabled = true;
                chip.setAttribute('aria-disabled', 'true');
                chip.style.opacity = '0.5';
                chip.style.cursor = 'not-allowed';
                // Half of these pickers are <div>s with an onclick, where
                // `disabled` means nothing — the handler would still fire and the
                // tool would happily encode at 320. pointer-events is what
                // actually closes the option there.
                if (chip.tagName !== 'BUTTON') chip.style.pointerEvents = 'none';
                locked++;
                if (chip.classList.contains('active') || chip.getAttribute('aria-pressed') === 'true') activeLocked = chip;
            } else if (val > bestVal) { best = chip; bestVal = val; }
        }

        // The selected chip is out of reach (default markup, a restored setting,
        // an expired plan) → land on the best allowed one THROUGH THE TOOL, then
        // say so. Guarded so the synthetic click can't re-enter this function.
        if (activeLocked && best && !box.__qtMoving) {
            box.__qtMoving = 1;
            try { best.click(); } catch (_) {}
            box.__qtMoving = 0;
            showLoweredNote(box, (best.textContent || '').replace(/\s+/g, ' ').trim(), kind);
        }
        renderPlanLine(box, kind, ceiling);
        if (locked > 0) emitImpression(kind, locked, ceiling);

        // These menus are rebuilt from JS on every format change (trim, volume,
        // silence-remover, add-silence rewrite innerHTML wholesale), which would
        // hand back ungated chips. Re-gate on any change to the group's children.
        if (!box.__qtObserved && typeof MutationObserver === 'function') {
            box.__qtObserved = true;
            var mo = new MutationObserver(function () {
                if (box.__qtGating || box.__qtMoving) return;
                box.__qtGating = true;
                try { gateGroup(box, kind, attr); } finally { box.__qtGating = false; }
            });
            try { mo.observe(box, { childList: true, subtree: true }); } catch (_) {}
        }
    }

    window.__qualityTier = {
        gate: gate,
        gateGroup: gateGroup,
        /* Тул ПРОГРАММНО поставил значение гейтованного <select> (восстановил
         * проект или черновик, отменил шаг): value выставлен без события, и
         * гейт его не видит. recheck() — пройти гейт заново: если это
         * сохранённый под бесплатным потолком выбор, а человек уже Премиум,
         * он поднимется обратно — с тем же тостом. */
        recheck: function (sel) {
            if (!sel || !sel.getAttribute) return;
            var k = sel.getAttribute('data-quality-gate');
            if (!k) return;
            sel.__qtRecheck = true;
            try { gate(sel, k); } finally { sel.__qtRecheck = false; }
        },
        /* Человек выбрал значение НЕ в самом <select> (диалог тула: «сделать
         * в 720p», «понизить разрешение») — это его решение, память о «хотел»
         * больше не действует, иначе следующий проход гейта его отменит. */
        chosen: function (sel) {
            var k = sel && sel.getAttribute && sel.getAttribute('data-quality-gate');
            if (k) forgetWanted(k);
        },
        /* Render the «your plan exports X · Premium: up to Y» line under an
         * arbitrary anchor — for a bespoke picker that is neither a <select> nor
         * a chip row (e.g. /record's segmented tiers). Same copy, same numbers. */
        planLine: function (anchor, kind, opts) { renderPlanLine(anchor, kind, cap(kind), opts); },
        /* Localized plan name («Премиум») for a bespoke picker's own badge, so a
         * tool never hardcodes it (feedback_plan_naming_premium_not_pro). */
        planName: function () { return tr('badge'); },
        /* Число в единицах оси так, как его пишет строка тарифа («320 кбит/с»),
         * — чтобы фраза тула о его собственном потолке не говорила другими
         * словами, чем строка тарифа рядом. null — у оси нет подписи. */
        unit: function (kind, n) { return unitLabel(kind, n); },
        cap: cap,
        isPro: isPro,
        audioMaxKbps:  function () { return cap('kbps'); },
        audioMaxBits:  function () { return cap('bits'); },
        audioMaxHz:    function () { return cap('hz'); },
        videoMaxHeight: function () { return cap('height'); },
        /* Long-side ceiling for image exports (px). Callers must treat Pro as
         * uncapped via isPro()/clamp(), same as every other kind. */
        imageMaxSide:  function () { return cap('side'); },
        /* Clamp a numeric request to the ceiling — for code paths that build a
         * value instead of picking from a <select> (e.g. an ffmpeg arg array).
         * Callers MUST still disclose; this is not a licence to downgrade quietly. */
        /* ⚠️ `opts.ownStream` — кадры, которые ВКЛЕИВАЮТСЯ в собственный поток
         * человека, а не экспорт (Фарид 07.10.2026: «делай как предлагаешь»).
         *
         * Единственный такой случай — затухание MP3 без перекодирования файла
         * (mp3-frame-trim.js): файл остаётся байтами исходника на ЕГО битрейте
         * (этот лейн потолком не зажимался никогда — он ничего не кодирует), а
         * заново кодируются только секунды под нарастанием и затуханием. Зажать
         * их — значит вшить в файл на 320 кбит/с десять секунд на 128: по замеру
         * ошибка под затуханием −17…−23 дБ против −31…−38 без потолка, то есть
         * хуже даже прежней «лесенки» (−27), которую эта вставка заменила.
         * Битрейт вставки назначает сам лейн — в полтора раза выше кадров,
         * которые она заменяет: это цена повторного кодирования, а не качество
         * сверх исходника. Плата за это — суточный кап тула, а не потолок.
         *
         * Как и `paid`, флаг путешествует с вызовом; глобального режима нет.
         * Кто вправе его ставить, держит OwnStreamCeilingExemptionTest. */
        clamp: function (kind, requested, opts) {
            /* ⚠️ `opts.paid` — выход, КУПЛЕННЫЙ ТОКЕНАМИ, а не тарифом.
             *
             * Потолок продаёт качество ТАРИФУ. Токены — отдельный рельс
             * (config/paid-tools.php, 402 insufficient_tokens), Премиум их не
             * даёт и они не дают Премиума; человек платит за КОНКРЕТНУЮ выдачу.
             * Зажать её потолком тарифа — взять деньги за файл и отдать файл
             * хуже. Замер 18.09.2026 на живой странице /text-to-speech, гость:
             * запрошено 192 → ffprobe 128000 на настоящих байтах.
             *
             * Флаг ставит только тот вызов, который ЗНАЕТ, что выдача оплачена,
             * и он путешествует вместе со значением — глобального режима
             * «сейчас у нас платно» нет намеренно: exec последователен, и
             * такой режим утёк бы на соседний бесплатный экспорт. Сторож —
             * PaidOutputIsNotClampedTest. */
            if (opts && opts.paid === true) return requested;
            if (opts && opts.ownStream === true) return requested;   // вставка в свой же поток — см. выше
            var c = cap(kind);
            if (isPro() || c === Infinity) return requested;
            return (typeof requested === 'number' && requested > c) ? c : requested;
        },
        /* How a caller PAYS the debt clamp() names, when the value it lowered was
         * the user's own file. Returns the sentence and the three numbers, or
         * null when nothing was lowered (Premium, no ceiling, source below it).
         * Rendering is the caller's job: this fact becomes true at different
         * moments in different tools — on /repair-audio only after the encode,
         * next to the "193 kbps → 129 kbps" row the user is already reading.
         *
         * Measured 2026-09-18 before this existed: a 193 kbps mp3 came back at
         * 129 with "Repair Complete", and the session emitted NO quality event of
         * any kind — the ceiling that had just cost the user a third of their
         * bitrate was invisible to the funnel AND to us. */
        sourceClamped: function (kind, requested, opts) {
            var c = cap(kind);
            var slot = 'quality-src:' + kind;
            /* Nothing lowered — and that includes "the NEXT file was smaller".
               A row published for the previous export would otherwise sit there
               describing a file the person no longer has. Retract, always. */
            if (isPro() || c === Infinity || typeof requested !== 'number' || !(requested > c)) {
                var slug0 = sharedBlock();
                if (slug0) { try { window.__toolLimit.setDetail(slug0, slot, ''); } catch (_) {} }
                if (_srcSaid[kind]) {
                    delete _srcSaid[kind];
                    /* The specific sentence is gone — the general one it stood in
                       for comes back: the «lowered» note if the gate had moved a
                       setting down, else the plan line. */
                    if (_lowered[kind] && _loweredText[kind] && slug0) {
                        try { window.__toolLimit.setDetail(slug0, 'quality-lowered:' + kind, _loweredText[kind]); } catch (_) {}
                    } else {
                        publishPlanRow(kind);
                    }
                }
                return null;
            }
            var top = paidCap(kind);
            var key = curSlug() + '|src|' + kind;
            if (!_srcClamped[key]) {
                _srcClamped[key] = 1;
                try {
                    window.__ae && window.__ae('feature_use', {
                        slug: curSlug(), f: 'quality_source_clamped',
                        kind: kind, src: Math.round(requested), out: c,
                        top: (top === Infinity ? null : top)
                    });
                } catch (_) {}
            }
            var out = {
                kind: kind, src: requested, out: c, top: (top === Infinity ? null : top),
                text: tr('src_clamped', {
                    src: unitLabel(kind, requested), out: unitLabel(kind, c),
                    top: unitLabel(kind, top === Infinity ? c : top), plan: tr('badge')
                })
            };
            /* Where it is SAID, by default: the shared limit block, the same row
               channel the sibling "Quality lowered to X" note uses — so a tool
               that offers "source" quality discloses without writing a line of
               its own. `render:false` is for a tool that has a better spot (on
               /repair-audio the sentence belongs under the bitrate row it
               explains, and saying it twice is what this channel avoids). */
            /* `render:false` — the tool shows the sentence at its own spot. It is
               still THE sentence for this kind: the block withholds its general
               rows while it stands. The contract that pays for it: the tool calls
               sourceClamped again whenever its sentence goes (another file, sound
               muted, a lower pick — pass 0), and the null answer brings them back.
               quality-module.js already calls it on every source export. */
            var slug = sharedBlock();
            if (slug) {
                try {
                    if (!opts || opts.render !== false) window.__toolLimit.setDetail(slug, slot, out.text);
                    else window.__toolLimit.setDetail(slug, slot, '');
                    /* This sentence contains the ceiling AND the plan, so the
                       generic line and the «lowered» note would repeat it. */
                    _srcSaid[kind] = 1;
                    window.__toolLimit.setDetail(slug, 'quality:' + kind, '');
                    window.__toolLimit.setDetail(slug, 'quality-lowered:' + kind, '');
                } catch (_) {}
            } else if (opts && opts.render === false) {
                _srcSaid[kind] = 1;
            }
            return out;
        },
        /* Disclosure for a clamp that did NOT come from the user's own file: a
         * tool asked for a bitrate (its default, or a pick) and the ceiling
         * lowered it. The sentence is the plan line — "your plan exports X,
         * Premium up to Y" — because nothing here is known about their file.
         *
         * Called from FFmpegProcessor.getCodecArgs, i.e. from the one door every
         * audio tool encodes through, so a tool with no quality picker at all
         * (dictaphone, mute-video, voice-compressor, video-audio, recording-studio)
         * discloses without a line of its own. */
        noteCeilingApplied: function (kind) {
            if (isPro()) return;
            var key = curSlug() + '|ceil|' + kind;
            if (!_ceilNoted[key]) {
                _ceilNoted[key] = 1;
                try {
                    window.__ae && window.__ae('feature_use', {
                        slug: curSlug(), f: 'quality_ceiling_applied', kind: kind, top: cap(kind)
                    });
                } catch (_) {}
            }
            publishPlanRow(kind);
        },
        /* An upsell link that lands in the SAME funnel counter as every other
         * quality wall — a tool that builds its own <a> disappears from it. */
        ctaLink: function (kind, where) {
            if (!window.__PRO_HERE) return null;
            var a = document.createElement('a');
            a.href = proHref();
            a.textContent = tr('cta');
            a.style.cssText = 'color:var(--accent,#4da3ff);text-decoration:underline';
            wireCta(a, kind, where || 'source_clamped', cap(kind));
            return a;
        }
    };

    /* ── The last door: lamejs constructed by hand ──────────────────────────
     *
     * Nine blades and two modules build `new lamejs.Mp3Encoder(ch, rate, kbps)`
     * themselves instead of going through MP3Encoder — and MP3 is the format
     * people ask for most, so that is where the ceiling was leaking after both
     * FFmpeg doors were closed. Measured 2026-09-18: /equalizer handed an
     * anonymous visitor 192 kbps from a 194 kbps source while its own block
     * said 128.
     *
     * Patching eleven call sites is a list that rots; lamejs arrives as ONE
     * global, so the constructor is wrapped the moment it is assigned. A tool
     * that loads lamejs later, or a tool written next year, is covered without
     * knowing this exists. Premium and an unknown tier pass through untouched,
     * like every other door. */
    (function guardLamejs(){
        var _lame;
        var wrap = function (ns) {
            try {
                if (!ns || typeof ns.Mp3Encoder !== 'function' || ns.Mp3Encoder.__qtWrapped) return ns;
                var Orig = ns.Mp3Encoder;
                var Wrapped = function (channels, rate, kbps) {
                    try {
                        if (typeof kbps === 'number' && kbps > 0) {
                            var capped = cap('kbps') === Infinity || isPro() ? kbps : Math.min(kbps, cap('kbps'));
                            if (capped < kbps) {
                                kbps = capped;
                                if (window.__qualityTier && window.__qualityTier.noteCeilingApplied) {
                                    window.__qualityTier.noteCeilingApplied('kbps');
                                }
                            }
                        }
                    } catch (_) {}
                    return new Orig(channels, rate, kbps);
                };
                Wrapped.__qtWrapped = true;
                Wrapped.prototype = Orig.prototype;
                ns.Mp3Encoder = Wrapped;
            } catch (_) {}
            return ns;
        };
        /* ⚠️ The accessor alone is NOT enough, measured 2026-09-18: lame.min.js
           declares `var lamejs` at the top level of a classic script, and a
           top-level var REDEFINES an existing configurable property as a plain
           data property — the setter is wiped before it ever fires, and the
           descriptor goes from get,set to value,writable. (That is also why
           window.lamejs is a FUNCTION here, not an object.)

           So the guard also listens for the script itself. Load events do not
           bubble, but a capture-phase listener on the document sees them, and
           every one of these blades injects lamejs as a <script> tag. Both
           paths are idempotent. */
        var rewrap = function () { try { if (window.lamejs) wrap(window.lamejs); } catch (_) {} };
        try {
            document.addEventListener('load', function (e) {
                var t = e && e.target;
                if (t && t.tagName === 'SCRIPT' && /lame/i.test(t.src || '')) rewrap();
            }, true);
        } catch (_) {}
        try {
            if (window.lamejs) { wrap(window.lamejs); return; }
            Object.defineProperty(window, 'lamejs', {
                configurable: true,
                get: function () { return _lame; },
                set: function (v) { _lame = wrap(v); },
            });
        } catch (_) { /* a page that already froze the property keeps today's behaviour */ }
    })();

    /* The tier snapshot lands asynchronously (session-bootstrap fetch). Re-gate
     * everything already marked when it arrives, so a Premium user who was
     * briefly shown the guest ceiling gets their options back. */
    document.addEventListener('tier-bootstrap:ready', autoGate);

    /* Auto-gate any select that declares its kind declaratively. A tool can opt
     * in with a single attribute and write zero JS:
     *     <select data-quality-gate="kbps"> */
    function autoGate() {
        /* Before the run, not after it: a tool that can hand back a smaller file
           has to say so while the person still has the choice (Farid 2026-09-18,
           «должен знать об этом заранее»). Measured the same day: 18 of 30 audio
           tools said nothing about the ceiling anywhere on the page. */
        try { if (pageOffersLossyAudio()) publishPlanRow('kbps'); } catch (_) {}
        try {
            var nodes = document.querySelectorAll('[data-quality-gate]');
            for (var i = 0; i < nodes.length; i++) {
                gate(nodes[i], nodes[i].getAttribute('data-quality-gate'));
            }
            var groups = document.querySelectorAll('[data-quality-gate-group]');
            for (var g = 0; g < groups.length; g++) {
                gateGroup(groups[g], groups[g].getAttribute('data-quality-gate-group'), groups[g].getAttribute('data-qt-attr'));
            }
        } catch (_) {}
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', autoGate);
    } else {
        autoGate();
    }
    /* Late pass. Two things land after DOMContentLoaded and after the bootstrap
     * event: a tool that sets its default selection programmatically (e.g. the
     * bitrate nearest the source file), and the terminal fallback path where
     * tier-bootstrap:ready never fires. Same 1.8 s belt-and-braces the daily-cap
     * module uses; gate() is idempotent, so a third run costs nothing. */
    setTimeout(autoGate, 1800);
})();
